import { Progress } from "@heroui/react";
import { Check, X } from "lucide-react";
import WizardValidationBanner from "./WizardValidationBanner";

const CampaignModalWizardShell = ({
  steps,
  activeStep,
  progressValue,
  headerTitle,
  headerDescription,
  onClose,
  children,
  footer,
  contentClassName = "p-6",
  isEditMode,
  topFieldErrors = [],
}) => {
  const total = steps.length;

  return (
    <div className="flex h-[calc(100dvh-100px)] w-full flex-col lg:flex-row">
      {/* MOBILE / TABLET RESPONSIVENESS */}
      <div className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 lg:hidden">
        <div className="px-4 py-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {steps.map((step, index) => {
              const isComplete = index < activeStep;
              const isCurrent = index === activeStep;

              return (
                <div
                  key={step.key}
                  className={`flex min-w-max shrink-0 items-center gap-2 rounded-lg p-2 ${isCurrent ? "bg-secondary-ultra-light" : ""
                    }`}
                >
                  {isComplete ? (
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success-500">
                      <Check
                        size={12}
                        strokeWidth={3}
                        className="text-white"
                      />
                    </div>
                  ) : isCurrent ? (
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-medium text-white">
                      {index + 1}
                    </div>
                  ) : (
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-[1.5px] border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400">
                      {index + 1}
                    </div>
                  )}

                  <h4
                    className={`whitespace-nowrap text-sm ${isComplete
                        ? "font-semibold text-gray-500 dark:text-gray-400"
                        : isCurrent
                          ? "font-bold text-secondary"
                          : "font-medium text-gray-500 dark:text-gray-400"
                      }`}
                  >
                    {step.label}
                  </h4>
                </div>
              );
            })}
          </div>

          <div className="mt-4">
            <Progress
              aria-label="Campaign setup progress"
              value={progressValue}
              classNames={{
                track: "h-[3px] rounded bg-gray-200",
                indicator: "bg-secondary",
              }}
            />

            <div className="mt-2 flex items-center justify-between">
              <h6 className="text-xs text-gray-500">
                Step {activeStep + 1} of {total}
              </h6>

              <p className="text-xs text-gray-500">
                {progressValue}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* DESKTOP SIDEBAR (ORIGINAL) */}
      <div className="hidden h-full w-[20%] flex-col border-r border-r-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 pt-10 lg:flex">
        <div className="flex flex-1 flex-col gap-1.5 px-4">
          {steps.map((step, index) => {
            const isComplete = index < activeStep;
            const isCurrent = index === activeStep;

            return (
              <div
                key={step.key}
                className={`flex items-center gap-3 p-2 ${isCurrent ? "rounded-xl bg-secondary-ultra-light" : ""
                  }`}
              >
                {isComplete ? (
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success-500">
                    <Check
                      size={12}
                      strokeWidth={3}
                      className="text-white"
                    />
                  </div>
                ) : isCurrent ? (
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-['Inter'] font-medium text-white">
                    {index + 1}
                  </div>
                ) : (
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-[1.5px] border-gray-200 dark:border-gray-700 text-xs font-['Inter'] font-medium text-gray-500 dark:text-gray-400">
                    {index + 1}
                  </div>
                )}

                <h4
                  className={`text-[0.81rem] font-['Inter'] ${isComplete
                      ? "font-semibold text-gray-500 dark:text-gray-400"
                      : isCurrent
                        ? "font-bold text-secondary"
                        : "font-medium text-gray-500 dark:text-gray-400"
                    }`}
                >
                  {step.label}
                </h4>
              </div>
            );
          })}
        </div>

        <div className="mt-auto border-t border-t-gray-200 dark:border-gray-800 px-4 py-5">
          <div className="flex flex-col gap-1.5">
            <Progress
              aria-label="Campaign setup progress"
              value={progressValue}
              classNames={{
                base: "max-w-md",
                track: "h-[3px] rounded bg-gray-200",
                indicator: "bg-secondary",
              }}
            />

            <div className="flex items-center justify-between">
              <h6 className="text-[0.679rem] text-gray-500">
                Step {activeStep + 1} of {total}
              </h6>

              <p className="text-[0.679rem] text-gray-500">
                {progressValue}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ===========================
      MAIN CONTENT
      =========================== */}
      <div className="flex min-h-0 flex-1 flex-col lg:w-[80%]">
        <div className="flex shrink-0 items-center justify-between gap-8 border-b border-gray-200 dark:border-gray-800">
          <div className="flex flex-col gap-1 p-4 md:p-6">
            <h4 className="text-[1.25rem] font-semibold text-gray-900 dark:text-gray-100 md:text-[1.45rem]">
              {isEditMode && activeStep === 0
                ? "Edit your campaign"
                : headerTitle}
            </h4>

            <p className="text-[0.875rem] font-normal text-gray-500 dark:text-gray-400">
              {headerDescription}
            </p>
          </div>

          <div className="pr-4 md:pr-6">
            <button
              type="button"
              className="rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} className="text-gray-400 dark:text-gray-300" />
            </button>
          </div>
        </div>

        <WizardValidationBanner
          errors={topFieldErrors}
          className="mx-4 mt-4 shrink-0 md:mx-6"
        />

        <div className={`min-h-0 flex-1 overflow-y-auto ${contentClassName}`}>
          {children}
        </div>

        <div className="mt-auto shrink-0 border-t border-t-gray-200 dark:border-gray-800 px-4 py-4 md:px-6">
          {footer}
        </div>
      </div>
    </div>
  );
};

export default CampaignModalWizardShell;
